import { SAMPLE_PRODUCTS_IMAGE_BASE_URL, SAMPLE_PRODUCT_IDS_BY_TYPE, SAMPLE_PRODUCT_LIST, SampleProduct } from '../_data/sample-products';

/**
 * The first product the welcome flow adds. The payload is the one the setup checklist's "Add products" step
 * sends to Items v3 (business-setup-checklist.component.ts saveProductWithCategoryId), so the product behaves
 * exactly like one added there: sold on every channel, one SKU, stock at the default location.
 */
export interface FirstProductInput {
  name: string;
  price: number;
  quantity: number;
  /** A data URL or a picture address (http/https), or empty for no picture. */
  image?: string;
  categoryId: string | number;
  /** The business-setup record (departmentId, manufacturerId, vendorId, businessId). */
  setup: any;
  userId?: string;
  userLocations?: any[];
  sku: string;
  barcode: string;
  now?: Date;
}

export const DEFAULT_PRODUCT_CATEGORY = 'General';

const PRODUCT_IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp', 'avif'];
export const MAX_PRODUCT_IMAGE_BYTES = 5 * 1024 * 1024;

/** The same image rules as the checklist: no SVG (it can carry scripts), 5 MB at most. Returns an error message or null. */
export function productImageError(file: { name: string; type?: string; size: number }): string | null {
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  const isImage = !file.type || (file.type.indexOf('image/') === 0 && file.type !== 'image/svg+xml');
  if (!isImage || PRODUCT_IMAGE_EXTENSIONS.indexOf(ext) < 0) return 'Please upload a PNG, JPG, WEBP, GIF, BMP or AVIF image.';
  if (file.size > MAX_PRODUCT_IMAGE_BYTES) return 'The picture must be 5MB or smaller.';
  return null;
}

const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];
export const MAX_LOGO_BYTES = 5 * 1024 * 1024;

/** The same rules as the checklist's logo picker. Returns an error message or null. */
export function logoImageError(file: { type?: string; size: number }): string | null {
  if (LOGO_TYPES.indexOf(String(file.type || '').toLowerCase()) < 0) return 'Please upload a PNG, JPG, WEBP or SVG logo.';
  if (file.size > MAX_LOGO_BYTES) return 'The logo must be 5 MB or smaller.';
  return null;
}

/** Every sample product gets this stock; the price is random between SAMPLE_PRICE_MIN and SAMPLE_PRICE_MAX (whole numbers). */
export const SAMPLE_PRODUCT_QUANTITY = 5;
export const SAMPLE_PRICE_MIN = 5;
export const SAMPLE_PRICE_MAX = 40;
export const SAMPLE_NAME_PREFIX = 'Sample';

/** The sample products for a business-type id (welcome-business-types.ts), with the picture as a full URL. Empty when the id is unknown. */
export function sampleProductsFor(businessTypeId: number | null | undefined): Array<SampleProduct & { imageUrl: string }> {
  const ids = SAMPLE_PRODUCT_IDS_BY_TYPE[Number(businessTypeId)] || [];
  return ids
    .map((id) => SAMPLE_PRODUCT_LIST[id])
    .filter((p): p is SampleProduct => !!p)
    // "Sample" in the name tells the merchant these are examples to replace or delete.
    .map((p) => ({ ...p, name: `${SAMPLE_NAME_PREFIX} ${p.name}`, imageUrl: SAMPLE_PRODUCTS_IMAGE_BASE_URL + p.image }));
}

/** A whole-number price from 5 to 40, both included. `random` returns a number in [0, 1). */
export function sampleProductPrice(random: () => number = Math.random): number {
  return SAMPLE_PRICE_MIN + Math.floor(random() * (SAMPLE_PRICE_MAX - SAMPLE_PRICE_MIN + 1));
}

export function buildFirstProductModel(input: FirstProductInput): any {
  const { setup } = input;
  const now = input.now || new Date();
  const departmentId = setup?.departmentId || 1;
  const price = Number(input.price) || 0;
  // At this point there is one (the default) location, whose id is the business id itself.
  const locationId = (input.userLocations && input.userLocations[0]) ?? setup?.businessId ?? setup?.id ?? 1;
  const image = input.image || '';

  return {
    itemName: input.name,
    notes: '',
    descriptionURL: '',
    itemType: 1,
    soldAs: 1,
    ageRestriction: 0,
    gender: 0,
    allowDiscount: 1,
    isTaxable: false,
    isOpnDept: false,
    showNotesWhen: 0,
    printOnInvoice: false,
    printAsQRCodeOnReceipt: false,
    trackStock: true,
    isActive: true,
    createdAt: now,
    lastEdited: now,
    addedBy: input.userId,
    departmentId,
    categoryId: input.categoryId,
    manufacturerId: setup?.manufacturerId || 1,
    vendorId: setup?.vendorId || 1,
    allowNegativeStock: false,
    description: '',
    image,
    imageThumbnail: image,
    grouping: 0,
    isVariantProduct: false,
    crsItemSKUs: [{
      skuCode: input.sku,
      barcode: input.barcode,
      salePriceA: price,
      salePriceB: price,
      salePriceC: price,
      costPrice: 0,
      discountPrice: 0,
      taxRateId: 1,
      minLevel: 0,
      maxLevel: 0,
      reorderLevel: 0,
      minOrderQty: 1,
      maxOrderQty: 999,
      defaultPrice: 1,
      sellQuantity: 1,
      uomId: 1,
      caseQty: 1,
      autoPurchaseOrder: false,
      barcodes: [],
      skus: [],
      tags: []
    }],
    itemVariantOptions: [],
    itemVariantOptionValues: [],
    itemVariantCombinations: [],
    openingStocks: [{ locationId, skuCode: input.sku, quantity: Number(input.quantity) || 0, price }],
    itemPictures: image ? [{ orderNo: 1, picture: image, thumbnail: image, isFeature: true, isDefault: true }] : [],
    // Products added during setup are listed on every sales channel.
    sellOnline: true,
    productSellType: 'all',
    isReturnable: true
  };
}
